/**
 * FilterAutocomplete - Multi-select autocomplete with select all/clear
 */

import React from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Chip,
  Tooltip,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import {
  Check as CheckIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
} from "@material-ui/icons";
import {
  FilterCard,
  FilterHeader,
  FilterLabel,
  FilterActions,
  ActionButton,
} from "../styled";

/**
 * Filter autocomplete component with select all/clear functionality
 * @param {Object} props
 * @param {string} props.label - Filter label
 * @param {Array} props.options - Available options
 * @param {Array} props.selectedValues - Currently selected values
 * @param {Function} props.onChange - Callback when selection changes
 * @param {Function} props.getOptionLabel - Function to get display label
 * @param {Function} props.getOptionSelected - Function to compare options
 */
const FilterAutocomplete = ({
  label,
  options,
  selectedValues,
  onChange,
  getOptionLabel = (option) => String(option),
  getOptionSelected,
}) => {
  const handleSelectAll = () => {
    onChange([...options]);
  };

  const handleClear = () => {
    onChange([]);
  };

  const handleSelectOnly = (e, option) => {
    e.stopPropagation();
    onChange([option]);
  };

  const defaultGetOptionSelected = (option, value) => {
    if (typeof option === "object" && option !== null) {
      return getOptionLabel(option) === getOptionLabel(value);
    }
    return option === value;
  };

  return (
    <FilterCard elevation={0} variant="outlined">
      <FilterHeader>
        <FilterLabel variant="caption">
          {label} ({selectedValues.length}/{options.length})
        </FilterLabel>
        <FilterActions>
          <Tooltip title="Select All">
            <ActionButton size="small" onClick={handleSelectAll}>
              <CheckIcon fontSize="small" />
            </ActionButton>
          </Tooltip>
          <Tooltip title="Clear">
            <ActionButton size="small" onClick={handleClear}>
              <ClearIcon fontSize="small" />
            </ActionButton>
          </Tooltip>
        </FilterActions>
      </FilterHeader>
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={options}
        value={selectedValues}
        onChange={(_, newValue) => onChange(newValue)}
        getOptionLabel={getOptionLabel}
        getOptionSelected={getOptionSelected || defaultGetOptionSelected}
        renderOption={(option) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Typography variant="body2" style={{ flex: 1 }}>
              {getOptionLabel(option)}
            </Typography>
            <Tooltip title="Select only">
              <IconButton
                size="small"
                onClick={(e) => {
                  handleSelectOnly(e, option);
                  document.activeElement.blur();
                }}
                style={{
                  padding: 4,
                  marginLeft: 8,
                  border: "2px solid #1976d2",
                  borderRadius: 4,
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                }}
              >
                <FilterIcon style={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        renderTags={(value, getTagProps) =>
          value.length > 2 ? (
            <Chip
              size="small"
              label={`${value.length} selected`}
              {...getTagProps({ index: 0 })}
              onDelete={handleClear}
            />
          ) : (
            value.map((option, index) => (
              <Chip
                key={index}
                size="small"
                label={getOptionLabel(option)}
                {...getTagProps({ index })}
              />
            ))
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={
              selectedValues.length === 0 ? `Select ${label}...` : ""
            }
          />
        )}
      />
    </FilterCard>
  );
};

export default FilterAutocomplete;
